import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '../App';
import { Heart, LogOut, Calendar, MessageCircle, MessageSquare, Phone, Clock, CheckCircle, Video, ToggleLeft, ToggleRight, Settings, BarChart3, FileText, Download, Users, AlertTriangle, Sparkles, CalendarClock } from 'lucide-react';
import VeyonikaAvailabilityModal from '../components/VeyonikaAvailabilityModal';
import { useNavigate } from 'react-router-dom';
import ChatSummary from '../components/ChatSummary';
import VeyonikaSessionSummary from '../components/VeyonikaSessionSummary';

export default function ProviderDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [sessions, setSessions] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [isAvailable, setIsAvailable] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [showVeyonikaSummary, setShowVeyonikaSummary] = useState(false);
  const [veyonikaSummaries, setVeyonikaSummaries] = useState([]);
  const pollInterval = useRef(null);

  useEffect(() => {
    fetchData();
    fetchSummaries();
    if (user) {
      setIsAvailable(user.is_available !== false);
    }

    // Poll for new sessions every 3 seconds
    pollInterval.current = setInterval(fetchData, 3000);

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [user]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [sessionsRes, appointmentsRes] = await Promise.all([
        axios.get(`${API}/sessions`, { headers }),
        axios.get(`${API}/appointments`, { headers })
      ]);
      setSessions(sessionsRes.data);
      setAppointments(appointmentsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummaries = async () => {
    try {
      const token = localStorage.getItem('token');
      const [genericRes, veyRes] = await Promise.all([
        axios.get(`${API}/summaries`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/veyonika-summaries`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setSummaries(genericRes.data);
      setVeyonikaSummaries(veyRes.data || []);
    } catch (error) {
      console.error('Failed to fetch summaries');
    }
  };

  const openSummary = (sessionId) => {
    setSelectedSessionId(sessionId);
    setShowSummary(true);
  };

  const openVeyonikaSummary = (sessionId) => {
    setSelectedSessionId(sessionId);
    setShowVeyonikaSummary(true);
  };

  const toggleAvailability = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/providers/availability`, {
        is_available: !isAvailable
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAvailable(!isAvailable);
      toast.success(`You are now ${!isAvailable ? 'available' : 'unavailable'}`);
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/appointments/${appointmentId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Appointment status updated');
      fetchData();
    } catch (error) {
      console.error('Update appointment error:', error);
      toast.error('Failed to update appointment');
    }
  };

  const startAppointmentSession = async (appointmentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/appointments/${appointmentId}/start-session`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const session = response.data;
      toast.success('Session started! Redirecting to video call...');
      navigate(`/call/${session.id}`);
    } catch (error) {
      console.error('Start session error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start session');
    }
  };

  const confirmAndStartSession = async (appointmentId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Step 1: Confirm the appointment
      await axios.put(`${API}/appointments/${appointmentId}/status?status=confirmed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Step 2: Start the session immediately
      const response = await axios.post(`${API}/appointments/${appointmentId}/start-session`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const session = response.data;
      toast.success('Session started! Redirecting to video call...');
      navigate(`/call/${session.id}`);
    } catch (error) {
      console.error('Confirm and start error:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm and start session');
      // Refresh data in case confirm succeeded but start failed
      fetchData();
    }
  };

  const stats = [
    {
      label: 'Pending Sessions',
      value: sessions.filter(s => s.status === 'pending' || s.status === 'queued').length,
      icon: Clock,
      color: '#F6E05E'
    },
    {
      label: 'Active Sessions',
      value: sessions.filter(s => s.status === 'active').length,
      icon: MessageCircle,
      color: '#64B5F6'
    },
    {
      label: 'Completed',
      value: sessions.filter(s => s.status === 'completed').length,
      icon: CheckCircle,
      color: '#81C784'
    },
    {
      label: 'Appointments',
      value: appointments.filter(a => a.status === 'scheduled' || a.status === 'confirmed' || a.status === 'in_progress').length,
      icon: Calendar,
      color: '#E89A8E'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA] theme-provider overflow-x-hidden">
      {/* Role Indicator Banner - Mobile Optimized */}
      <div className="bg-blue-600 text-white py-2 px-2 sm:px-4 text-center sticky top-0 z-[60]">
        <div className="container mx-auto flex items-center justify-center gap-1 sm:gap-3 flex-wrap">
          <span className="bg-white text-blue-600 font-bold px-2 py-0.5 rounded-full text-xs sm:text-sm">CG</span>
          <span className="font-medium text-xs sm:text-sm">Care Giver</span>
          <span className="text-blue-100 text-xs truncate max-w-[120px] sm:max-w-none">{user?.email}</span>
        </div>
      </div>
      
      {/* Header with Provider Theme - Mobile Optimized */}
      <header className="dashboard-header text-white sticky top-[32px] sm:top-[40px] z-50">
        <div className="container mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="text-white" size={24} />
            <h1 className="text-base sm:text-xl font-heading font-semibold hidden sm:block">Veyonika</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-6">
            <button
              data-testid="availability-toggle"
              onClick={toggleAvailability}
              className="flex items-center gap-1 sm:gap-2"
            >
              {isAvailable ? (
                <>
                  <ToggleRight size={22} className="text-green-300" />
                  <span className="text-xs sm:text-sm font-medium text-green-300 hidden sm:inline">Available</span>
                </>
              ) : (
                <>
                  <ToggleLeft size={22} className="text-white/50" />
                  <span className="text-xs sm:text-sm font-medium text-white/50 hidden sm:inline">Unavailable</span>
                </>
              )}
            </button>
            <button
              data-testid="my-schedule-btn"
              onClick={() => setShowSchedule(true)}
              className="flex items-center gap-1 sm:gap-2 text-white/80 hover:text-white transition-colors"
              title="Manage my weekly availability"
            >
              <CalendarClock size={20} />
              <span className="text-xs sm:text-sm font-medium hidden sm:inline">My Schedule</span>
            </button>
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                data-testid="admin-link"
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                title="Admin Dashboard"
              >
                <BarChart3 size={18} />
              </button>
              {/* MVP Admin Links */}
              {(user?.role === 'admin' || user?.base_role === 'admin') && (
                <>
                  <button
                    onClick={() => navigate('/admin/users')}
                    className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                    title="User Management"
                  >
                    <Users size={18} />
                  </button>
                  <button
                    onClick={() => navigate('/escalations')}
                    className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                    title="Escalations"
                  >
                    <AlertTriangle size={18} />
                  </button>
                </>
              )}
              {(user?.sub_role === 'supervisor' || user?.sub_role === 'student_welfare_head') && (
                <button
                  onClick={() => navigate('/escalations')}
                  className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                  title="Escalations"
                >
                  <AlertTriangle size={18} />
                </button>
              )}
              <button
                data-testid="profile-link"
                onClick={() => navigate('/profile-settings')}
                className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                title="Profile Settings"
              >
                <Settings size={18} />
              </button>
              <button
                data-testid="logout-btn"
                onClick={logout}
                className="flex items-center gap-2 text-white/80 hover:text-red-300 transition-colors"
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-3 sm:px-6 py-6 sm:py-12">
        {/* Welcome Section - Mobile Optimized */}
        <div className="mb-6 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-[#2D3748] mb-2">
            Welcome, Dr. {user?.name?.split(' ')[0]}
          </h2>
          <p className="text-base sm:text-lg text-[#718096]">
            {user?.specialization && `Specialization: ${user.specialization}`}
          </p>
        </div>

        {/* Stats Grid - Mobile Optimized */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-8 sm:mb-12">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="card p-6">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${stat.color}20` }}
                >
                  <Icon size={24} style={{ color: stat.color }} />
                </div>
                <div className="text-3xl font-heading font-semibold text-[#2D3748] mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-[#718096]">{stat.label}</div>
              </div>
            );
          })}
        </div>

        {/* Pending Requests */}
        <div className="mb-8">
          <h3 className="text-2xl font-heading font-semibold text-[#2D3748] mb-4">Pending Requests</h3>
          {sessions.filter(s => s.status === 'pending' || s.status === 'queued').length === 0 ? (
            <div className="card p-8 text-center text-[#718096]">
              <Clock size={48} className="mx-auto mb-4 opacity-30" />
              <p>No pending requests</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions
                .filter(s => s.status === 'pending' || s.status === 'queued')
                .map((session) => {
                  const isChat = session.channel === 'chat' || session.session_type === 'anonymous_chat';
                  const isVoice = session.channel === 'voice' || session.session_type === 'anonymous_voice';
                  const isVideo = session.channel === 'video';
                  const displayName = session.grade_code || session.session_type?.replace('_', ' ') || 'Session';
                  
                  return (
                    <div key={session.id} data-testid={`session-${session.id}`} className="card p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {isChat ? (
                            <MessageCircle className="text-primary" size={24} />
                          ) : isVoice ? (
                            <Phone className="text-accent" size={24} />
                          ) : (
                            <Video className="text-info" size={24} />
                          )}
                          <div>
                            <h4 className="font-medium text-[#2D3748] capitalize">
                              {displayName}
                            </h4>
                            <p className="text-sm text-[#718096]">
                              Requested {new Date(session.created_at).toLocaleString()}
                            </p>
                            {session.mood_tags && session.mood_tags.length > 0 && (
                              <div className="flex gap-1 mt-1 flex-wrap">
                                {session.mood_tags.map(tag => (
                                  <span key={tag} className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full capitalize">
                                    {tag.replace('_', ' ')}
                                  </span>
                                ))}
                              </div>
                            )}
                            {session.presenting_concern && (
                              <p
                                className="text-xs text-[#4A5568] mt-2 italic line-clamp-1 max-w-md"
                                title={session.presenting_concern}
                                data-testid={`presenting-concern-${session.id}`}
                              >
                                <span className="not-italic font-medium text-[#718096] mr-1">What brought them here:</span>
                                {session.presenting_concern.length > 120
                                  ? `${session.presenting_concern.slice(0, 120)}…`
                                  : session.presenting_concern}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {isChat && (
                            <button
                              data-testid={`accept-chat-${session.id}`}
                              onClick={() => navigate(`/chat/${session.id}`)}
                              className="btn-primary text-sm"
                            >
                              Accept Chat
                            </button>
                          )}
                          {(isVoice || isVideo) && (
                            <button
                              data-testid={`join-call-${session.id}`}
                              onClick={() => navigate(`/call/${session.id}`)}
                              className="btn-primary text-sm flex items-center gap-2"
                            >
                              {isVoice ? <Phone size={16} /> : <Video size={16} />}
                              Join {isVoice ? 'Voice' : 'Video'} Call
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Active Sessions */}
        {sessions.filter(s => s.status === 'active').length > 0 && (
          <div className="mt-8">
            <h3 className="text-2xl font-heading font-semibold text-[#2D3748] mb-4 flex items-center gap-2">
              <MessageCircle className="text-green-500" size={24} />
              Active Sessions
              <span className="text-sm px-2 py-1 bg-green-100 text-green-700 rounded-full">
                {sessions.filter(s => s.status === 'active').length} in progress
              </span>
            </h3>
            <div className="space-y-4">
              {sessions
                .filter(s => s.status === 'active')
                .map((session) => {
                  const isChat = session.channel === 'chat';
                  const isVoice = session.channel === 'voice';
                  const displayName = session.grade_code || session.session_type?.replace('_', ' ') || 'Session';
                  
                  return (
                    <div key={session.id} className="card p-6 border-l-4 border-green-500 bg-green-50/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {isChat ? (
                            <MessageCircle className="text-green-500" size={24} />
                          ) : isVoice ? (
                            <Phone className="text-green-500" size={24} />
                          ) : (
                            <Video className="text-green-500" size={24} />
                          )}
                          <div>
                            <h4 className="font-medium text-[#2D3748] capitalize">
                              {displayName} - In Progress
                            </h4>
                            <p className="text-sm text-[#718096]">
                              Started: {session.started_at ? new Date(session.started_at).toLocaleString() : 'Just now'}
                            </p>
                            <p className="text-sm text-[#718096]">
                              Client: {session.masked_id || `ANON-${session.seeker_id?.slice(0,8).toUpperCase()}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openVeyonikaSummary(session.id)}
                            className="text-sm flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:opacity-90 transition-opacity"
                            data-testid={`active-veyonika-summary-btn-${session.id}`}
                          >
                            <Sparkles size={16} />
                            Veyonika Summary
                          </button>
                          <button
                            onClick={() => navigate(isChat ? `/chat/${session.id}` : `/call/${session.id}`)}
                            className="btn-primary text-sm flex items-center gap-2 bg-green-600 hover:bg-green-700"
                          >
                            {isChat ? (
                              <>
                                <MessageCircle size={16} />
                                Continue Chat
                              </>
                            ) : (
                              <>
                                <Phone size={16} />
                                Continue Call
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Upcoming Appointments */}
        <div>
          <h3 className="text-2xl font-heading font-semibold text-[#2D3748] mb-4">Upcoming Appointments</h3>
          {appointments.filter(a => a.status === 'scheduled' || a.status === 'confirmed' || a.status === 'in_progress').length === 0 ? (
            <div className="card p-8 text-center text-[#718096]">
              <Calendar size={48} className="mx-auto mb-4 opacity-30" />
              <p>No upcoming appointments</p>
            </div>
          ) : (
            <div className="space-y-4">
              {appointments
                .filter(a => a.status === 'scheduled' || a.status === 'confirmed' || a.status === 'in_progress')
                .map((appointment) => (
                  <div key={appointment.id} data-testid={`appointment-${appointment.id}`} className="card p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <Calendar className="text-primary" size={24} />
                        <div>
                          <h4 className="font-medium text-[#2D3748] capitalize">
                            {appointment.session_type} Session
                          </h4>
                          <p className="text-sm text-[#718096]">
                            {appointment.scheduled_date} at {appointment.scheduled_time}
                          </p>
                          {appointment.notes && (
                            <p className="text-sm text-[#718096] mt-1">Note: {appointment.notes}</p>
                          )}
                        </div>
                      </div>
                      <span className="text-sm px-3 py-1 rounded-full bg-primary/10 text-primary capitalize">
                        {appointment.status}
                      </span>
                    </div>
                    {appointment.status === 'scheduled' && (
                      <div className="flex gap-2 items-center">
                        <button
                          data-testid={`confirm-appointment-${appointment.id}`}
                          onClick={() => confirmAndStartSession(appointment.id)}
                          className="btn-primary text-sm flex items-center gap-2 bg-green-600 hover:bg-green-700"
                        >
                          <Video size={16} />
                          Confirm & Join Call
                        </button>
                        <button
                          onClick={() => updateAppointmentStatus(appointment.id, 'cancelled')}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Decline
                        </button>
                      </div>
                    )}
                    {appointment.status === 'confirmed' && (
                      <div className="flex gap-2">
                        <button
                          data-testid={`start-session-${appointment.id}`}
                          onClick={() => startAppointmentSession(appointment.id)}
                          className="btn-primary text-sm flex items-center gap-2 bg-green-600 hover:bg-green-700 animate-pulse"
                        >
                          <Video size={16} />
                          Start Video Call
                        </button>
                      </div>
                    )}
                    {appointment.status === 'in_progress' && appointment.session_id && (
                      <button
                        data-testid={`join-session-${appointment.id}`}
                        onClick={() => navigate(`/call/${appointment.session_id}`)}
                        className="btn-primary text-sm flex items-center gap-2 bg-green-600 hover:bg-green-700"
                      >
                        <Video size={16} />
                        Rejoin Session
                      </button>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
        
        {/* Completed Appointments */}
        {appointments.filter(a => a.status === 'completed').length > 0 && (
          <div className="mt-8">
            <h3 className="text-2xl font-heading font-semibold text-[#2D3748] mb-4">
              <Calendar className="inline mr-2 text-green-500" size={24} />
              Completed Appointments
            </h3>
            <div className="space-y-4">
              {appointments
                .filter(a => a.status === 'completed')
                .slice(0, 5)
                .map((appointment) => (
                  <div key={appointment.id} className="card p-6 opacity-75 border-l-4 border-green-400">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <CheckCircle className="text-green-500" size={24} />
                        <div>
                          <h4 className="font-medium text-[#2D3748] capitalize">
                            {appointment.session_type} Session
                          </h4>
                          <p className="text-sm text-[#718096]">
                            {appointment.scheduled_date} at {appointment.scheduled_time}
                          </p>
                          {appointment.notes && (
                            <p className="text-sm text-[#718096] mt-1">Note: {appointment.notes}</p>
                          )}
                        </div>
                      </div>
                      <span className="text-sm px-3 py-1 rounded-full bg-green-100 text-green-700">
                        Completed
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Completed Sessions - Need Summary */}
        <div className="mt-8">
          <h3 className="text-2xl font-heading font-semibold text-[#2D3748] mb-4">
            <CheckCircle className="inline mr-2" size={24} />
            Completed Sessions
          </h3>
          {sessions.filter(s => s.status === 'completed' && !s.has_summary).length === 0 ? (
            <div className="card p-8 text-center text-[#718096]">
              <CheckCircle size={48} className="mx-auto mb-4 opacity-30" />
              <p>All completed sessions have summaries</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions
                .filter(s => s.status === 'completed' && !s.has_summary)
                .map((session) => (
                  <div key={session.id} className="card p-6 border-l-4 border-orange-400">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <MessageSquare className="text-orange-500" size={24} />
                        <div>
                          <h4 className="font-medium text-[#2D3748]">
                            {session.grade_code || 'Chat'} Session
                          </h4>
                          <p className="text-sm text-[#718096]">
                            Client: {session.masked_id || `ANON-${session.seeker_id?.slice(0,8).toUpperCase()}`}
                          </p>
                          <p className="text-sm text-[#718096]">
                            Completed: {new Date(session.ended_at || session.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openSummary(session.id)}
                          className="btn-primary text-sm flex items-center gap-2"
                          data-testid={`create-summary-btn-${session.id}`}
                        >
                          <FileText size={16} />
                          Create Summary
                        </button>
                        {/* Veyonika-specific end-of-session note (internal clinical template) */}
                        <button
                          onClick={() => openVeyonikaSummary(session.id)}
                          className="text-sm flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:opacity-90 transition-opacity"
                          data-testid={`create-veyonika-summary-btn-${session.id}`}
                        >
                          <Sparkles size={16} />
                          Veyonika Summary
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Session History & Summaries */}
        <div className="mt-8">
          <h3 className="text-2xl font-heading font-semibold text-[#2D3748] mb-4">
            <FileText className="inline mr-2" size={24} />
            Session Summaries
          </h3>
          {summaries.length === 0 ? (
            <div className="card p-8 text-center text-[#718096]">
              <FileText size={48} className="mx-auto mb-4 opacity-30" />
              <p>No session summaries yet</p>
              <p className="text-sm mt-2">Summaries will appear here after you complete chat sessions</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {summaries.map((summary) => (
                <div 
                  key={summary.id} 
                  className="card p-4 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => openSummary(summary.session_id)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium text-[#2D3748]">{summary.masked_id}</p>
                      <p className="text-sm text-[#718096]">{summary.session_date} at {summary.session_time}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      summary.risk_level === 'high' ? 'bg-red-100 text-red-700' :
                      summary.risk_level === 'medium' ? 'bg-orange-100 text-orange-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {summary.risk_level.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {(summary.key_topics || []).slice(0, 3).map((topic, idx) => (
                      <span key={idx} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">
                        {topic}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#718096]">
                      {summary.grade_code || 'Chat'} • {summary.session_duration_minutes || '?'} min
                    </span>
                    <button className="text-primary hover:underline flex items-center gap-1">
                      <FileText size={14} />
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chat Summary Modal */}
      <ChatSummary 
        sessionId={selectedSessionId}
        isOpen={showSummary}
        onClose={() => {
          setShowSummary(false);
          setSelectedSessionId(null);
          fetchSummaries();
        }}
      />

      {/* Veyonika Session Summary Modal (additional, internal-only template) */}
      <VeyonikaSessionSummary
        sessionId={selectedSessionId}
        isOpen={showVeyonikaSummary}
        onClose={() => {
          setShowVeyonikaSummary(false);
          setSelectedSessionId(null);
          fetchSummaries();
        }}
      />

      {/* Provider's own weekly availability (reuses the admin availability modal) */}
      <VeyonikaAvailabilityModal
        isOpen={showSchedule}
        providerId={user?.id}
        providerName={user?.name}
        token={localStorage.getItem('token')}
        onClose={() => setShowSchedule(false)}
      />
    </div>
  );
}